import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { users, whatsapp } from '../../services/api';

/*
 * Contacto por WhatsApp ao balcão. A API existe desde o módulo WhatsApp (POST /api/whatsapp/send, Admin),
 * mas não havia ecrã nenhum que a chamasse — o botão "WhatsApp" da Operação/Receção não fazia nada.
 *
 * O servidor distingue três falhas que pedem coisas diferentes a quem está ao balcão, e o diálogo diz
 * cada uma por palavras suas: número desligado (é preciso religar em Definições), sem configuração, e
 * falha de envio (repetível). Nunca diz "enviado" quando não foi.
 */
export default function WhatsAppDialog({ open, onClose }) {
  const [all, setAll] = useState([]);
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTerm(''); setSelected(null); setPhone(''); setMessage(''); setProblem(null); setLoadError(false);
    users.getAll()
      .then((r) => { if (alive) setAll(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [open]);

  const matches = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [];
    return all
      .filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [term, all]);

  if (!open) return null;

  const pick = (u) => {
    setSelected(u);
    setPhone(u.phone || '');
  };

  const send = async () => {
    if (!phone.trim()) { toast.warning('Indique o número (com indicativo, ex.: 351…)'); return; }
    if (message.trim().length < 2) { toast.warning('Escreva a mensagem'); return; }
    setBusy(true); setProblem(null);
    try {
      const res = await whatsapp.send({ phone: phone.trim(), message: message.trim() });
      if (res.data?.sent) {
        toast.success('Mensagem enviada');
        onClose();
      } else {
        setProblem({ kind: 'unknown', text: 'O envio não foi confirmado pelo gateway.' });
      }
    } catch (e) {
      const d = e.response?.data || {};
      const status = e.response?.status;
      if (d.needsReconnect) {
        setProblem({ kind: 'reconnect', text: d.message || 'O WhatsApp deste ginásio está desligado. Ligue o número outra vez em Definições.' });
      } else if (d.needsSetup) {
        setProblem({ kind: 'setup', text: d.error || 'O WhatsApp deste ginásio ainda não está configurado.' });
      } else if (status === 502) {
        setProblem({ kind: 'retry', text: `Falha no envio (${d.error || 'erro do gateway'}). Pode tentar outra vez.` });
      } else {
        setProblem({ kind: 'unknown', text: d.error || d.message || 'Não foi possível enviar a mensagem.' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="Enviar WhatsApp">
      <div style={S.panel}>
        <div style={S.head}>
          <h3 style={S.title}>Enviar WhatsApp</h3>
          <button type="button" style={S.close} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <label style={S.label} htmlFor="wa-search">Membro (opcional — também pode escrever o número)</label>
        <input id="wa-search" style={S.input} value={term} onChange={(e) => { setTerm(e.target.value); setSelected(null); }}
               placeholder="Nome ou email (mínimo 2 letras)" autoFocus />
        {loadError && <div style={S.error}>Não foi possível carregar a lista de membros.</div>}
        {!loadError && term.trim().length >= 2 && matches.length === 0 && <div style={S.hint}>Sem resultados.</div>}
        {matches.length > 0 && (
          <ul style={S.list}>
            {matches.map((u) => (
              <li key={u.id}>
                <button type="button" style={{ ...S.option, ...(selected?.id === u.id ? S.optionActive : {}) }} onClick={() => pick(u)}>
                  <span style={S.optionName}>{u.name || '(sem nome)'}</span>
                  <span style={S.optionMeta}>{u.phone ? `📱 ${u.phone}` : 'sem telefone no registo'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <label style={S.label} htmlFor="wa-phone">Número</label>
        <input id="wa-phone" style={S.input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="351912345678" />

        <label style={S.label} htmlFor="wa-msg">Mensagem</label>
        <textarea id="wa-msg" style={S.textarea} rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />

        {problem && (
          <div style={problem.kind === 'retry' ? S.warn : S.error2}>
            <strong>{problem.kind === 'reconnect' ? 'WhatsApp desligado' : problem.kind === 'setup' ? 'Por configurar' : 'Não enviado'}</strong>
            <div style={S.reason}>{problem.text}</div>
            {problem.kind === 'reconnect' && <div style={S.reason}>Definições → WhatsApp → ligar o número.</div>}
          </div>
        )}

        <div style={S.footer}>
          <button type="button" style={S.secondary} onClick={onClose}>Cancelar</button>
          <button type="button" style={S.primary} disabled={busy} onClick={send}>{busy ? 'A enviar…' : 'Enviar'}</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  panel: { background: 'var(--bg-card)', color: 'var(--text-primary)', borderRadius: 'var(--radius-lg, 14px)', padding: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg, 0 20px 40px rgba(0,0,0,.2))' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  close: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 6px', color: 'var(--text-secondary)' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-primary)', fontSize: 14 },
  list: { listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  option: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer' },
  optionActive: { borderColor: 'var(--accent-primary)', background: 'var(--bg-hover)' },
  optionName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  optionMeta: { fontSize: 12, color: 'var(--text-secondary)' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  primary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--accent-primary)', color: 'var(--text-inverse)', fontSize: 14, cursor: 'pointer' },
  secondary: { padding: '10px 14px', borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  hint: { fontSize: 13, color: 'var(--text-muted)', marginTop: 8 },
  error: { fontSize: 13, color: 'var(--error-text)', marginTop: 8 },
  error2: { border: '1px solid var(--error)', background: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14, marginTop: 12 },
  warn: { border: '1px solid var(--warning)', background: 'var(--warning-bg)', color: 'var(--warning-text)', borderRadius: 'var(--radius-md, 8px)', padding: 12, fontSize: 14, marginTop: 12 },
  reason: { fontSize: 13, marginTop: 4 },
};
